import { Link } from 'react-router-dom';
import { services } from '../../../mocks/services';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

export default function ServicesOverview() {
  const featuredServices = services.slice(0, 2);
  const gridServices = services.slice(2);

  return (
    <section className="bg-brand-cream py-24 relative">
      {/* Section top accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-green/30 via-brand-gold/50 to-brand-green/30" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <RevealOnScroll className="text-center mb-16">
          <span className="text-brand-green text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2">
            <span className="w-8 h-px bg-brand-green inline-block" />
            What We Do
            <span className="w-8 h-px bg-brand-green inline-block" />
          </span>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-brand-dark mt-4 mb-5">
            Complete Roofing &amp; Exterior<br />
            <span className="text-brand-green italic">Construction Services</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto leading-relaxed">
            From flat roof systems to shingle replacements, tuckpointing to vinyl siding — Green World Construction
            delivers premium workmanship across every exterior trade, backed by 35+ years of Chicagoland expertise.
          </p>
        </RevealOnScroll>

        {/* Featured 2 services — large format */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {featuredServices.map((service, idx) => (
            <RevealOnScroll key={service.id} delay={idx * 120} direction={idx === 0 ? 'left' : 'right'}>
              <Link
                to={`/services/${service.slug}`}
                className="group relative block rounded-xl overflow-hidden h-72 cursor-pointer"
              >
                <div className="w-full h-full">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent group-hover:from-black/80 transition-all duration-300" />
                {/* Category badge */}
                <div className="absolute top-4 left-4">
                  <span className="inline-flex items-center gap-1.5 bg-brand-gold/90 text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide">
                    <i className={`${service.icon} text-xs`} />
                    {service.category}
                  </span>
                </div>
                {/* Content */}
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="font-display text-2xl font-bold text-white mb-2">{service.name}</h3>
                  <p className="text-white/75 text-sm leading-relaxed mb-4 line-clamp-2">{service.shortDesc}</p>
                  <div className="flex items-center gap-2 text-brand-gold text-sm font-bold group-hover:gap-3 transition-all">
                    <span>Get Free Estimate</span>
                    <i className="ri-arrow-right-line" />
                  </div>
                </div>
              </Link>
            </RevealOnScroll>
          ))}
        </div>

        {/* 6-card grid for remaining services */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
          {gridServices.map((service, idx) => (
            <RevealOnScroll key={service.id} delay={idx * 80}>
              <Link
                to={`/services/${service.slug}`}
                className="group bg-white rounded-xl overflow-hidden border border-brand-cream-3 hover:border-brand-gold hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col"
              >
                {/* Image */}
                <div className="w-full h-48 overflow-hidden relative">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Service icon overlay */}
                  <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center bg-brand-dark/80 backdrop-blur-sm rounded-lg">
                    <i className={`${service.icon} text-brand-gold text-sm`} />
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-semibold text-brand-dark text-base mb-2">{service.name}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-4 flex-1">{service.shortDesc}</p>
                  {/* Features pills */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {service.features.slice(0, 3).map((f) => (
                      <span key={f} className="text-xs bg-brand-cream px-2.5 py-1 rounded-full text-gray-600 border border-brand-cream-3">
                        {f}
                      </span>
                    ))}
                  </div>
                  <span className="text-xs font-bold text-brand-green flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn More <i className="ri-arrow-right-line text-xs" />
                  </span>
                </div>
              </Link>
            </RevealOnScroll>
          ))}
        </div>

        {/* CTA */}
        <RevealOnScroll className="text-center">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white text-sm font-bold px-10 py-4 rounded-md transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-apps-2-line text-brand-gold" />
            Explore All Services
            <i className="ri-arrow-right-line" />
          </Link>
        </RevealOnScroll>
      </div>
    </section>
  );
}
